import { Formik, FormikProps } from "formik";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { Button } from "@/components/Button";
import { Text } from "@/components/Text";
import { IAttachment, TAttachmentPurpose } from "@/data/attachments";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { ImageSection } from "@/views/components/Files";
import { Messenger } from "@/views/components/Messenger";
import { Avatar } from "@/views/components/Person";

import { profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { IProfileFormikProps } from "../types";

import "./_avatar-form.scss";

export const validationAvatarSectionSchema = Yup.object().shape({});

export const AVATAR_MESSENGER_CONTEXT = "profile-user-general-form";

export interface IAvatarFormValues {
  avatar: IAttachment | null;
  readonly firstName: string;
  readonly lastName: string;
}

interface IAvatarFormProps {
  form: FormikProps<IAvatarFormValues>;
  source: IConsultantProfile;
  onCancel: () => void;
}

const AvatarForm: FC<IAvatarFormProps> = ({ form, onCancel }) => {
  const { t } = useTranslation("session");

  const baseClassName = "avatar-form";

  const renderAvatar = () => (
    <Avatar
      className={`${baseClassName}__avatar`}
      size="profile"
      avatar={form.values.avatar}
      firstName={form.values.firstName}
      lastName={form.values.lastName}
      testName="avatarro"
      hjSuppress
    />
  );

  return (
    <FormikForm className={baseClassName}>
      <Flex direction="column" innerMargin="md">
        <Messenger context={AVATAR_MESSENGER_CONTEXT} />

        <ImageSection
          attachmentType={TAttachmentPurpose.AVATAR}
          value={form.values.avatar}
          name="avatar"
          testName="avatar"
          renderPlaceholder={renderAvatar}
          renderImage={renderAvatar}
          renderDetails={() => (
            <Text color="grey1" size="md" align="center">
              {t("CONSULTANT_PROFILES.DETAIL.AVATAR.SHAPE")}
            </Text>
          )}
        />

        <Flex innerMargin="md" justify="flex-end">
          <Button variant="secondary" size="lg" testName="cancel" onClick={onCancel}>
            {t("CONSULTANT_PROFILES.DETAIL.AVATAR.BUTTON_CANCEL")}
          </Button>
          <Button size="lg" testName="save" onClick={form.submitForm} disabled={form.isSubmitting}>
            {t("CONSULTANT_PROFILES.DETAIL.AVATAR.BUTTON_SAVE")}
          </Button>
        </Flex>
      </Flex>
    </FormikForm>
  );
};

interface IAvatarFormikProps extends IProfileFormikProps<IAvatarFormValues>, IAvatarFormProps {}

export const AvatarFormik: React.FunctionComponent<IAvatarFormikProps> = React.memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<IAvatarFormValues>) => <AvatarForm {...rest} form={form} />}
    />
  ),
);

export const AvatarEdit = profilePartialUpdaterBuilder(AvatarFormik, {
  validationSchema: validationAvatarSectionSchema,
  messengerContext: AVATAR_MESSENGER_CONTEXT,
});
